/**
 * Core-100 R4 (CORE-AV) — ONE assembler of the load index's inputs for a shop-day.
 *
 * Constitution CORE-AV: "One implementation serves every surface" — "a load figure that differs between the calendar
 * and the board is not a measurement". The formula (`calculateOBLI`) was already shared, but each surface assembled
 * its own inputs: the Dayboard required `scheduled_date` while the calendar fell back to `start_time`; the Dayboard
 * counted ACTIVE mechanical technicians while the calendar counted every mechanical row; the two read shop hours
 * differently; and both measured short-staffing as a HEAD COUNT, so a technician working two hours of an eight-hour
 * day counted as fully present (CORE-AV-GAP-005, "account for … reduced staffing").
 *
 * This module is the single assembler. Both the Dayboard indicator and the calendar bucket call it, so the same
 * shop-day yields the same `OBLIInput` (and so the same `OBLIResult`) on both. Staffing stress is measured in
 * MINUTES: the scheduled productive minutes of the mechanical roster (absences, time off and breaks subtracted by
 * the capacity authority) against the nominal minutes the same roster would have worked with no absence.
 */
import { resolveTechnicianCapacity } from '@/utils/capacity/technicianCapacityState';
import { countsAsMechanicalCapacity, sumEffectiveWorkload } from '@/utils/dayEffectiveLoad';
import { isOnBacklogBoard } from '@/utils/backlogState';
import { isTicketDeleted } from '@/utils/ticketDeletedState';
import { getShopDayLabel } from '@/utils/shopTime';
import { isOBLICapacityKnown, type OBLIInput } from '@/utils/obliCalculation';

const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

export interface OBLIDayArgs {
  technicians: any[];
  tickets: any[];
  selectedDate: Date;
  /** Canonical shop-local day label (yyyy-MM-dd). */
  dayLabel: string;
  shopTimezone?: string | null;
  shopHours?: Record<string | number, any> | null;
  holidays?: string[];
  mechanicalTechnicianIds?: ReadonlySet<string> | null;
  capacityInputsReady?: boolean;
  nowMs: number;
}

export interface OBLIDay {
  input: OBLIInput;
  dayTickets: any[];
  /** Active mechanical roster (the staffing denominator). */
  roster: any[];
  workingTechs: any[];
  unknownHoursCount: number;
  totalCapacity: number;
  nominalCapacity: number;
}

/** Shop-hours lookup tolerant of the three key formats the product writes (numeric, lowercase, Capitalized). */
export function shopHoursForDay(shopHours: Record<string | number, any> | null | undefined, dayOfWeek: number) {
  if (!shopHours) return undefined;
  const lower = DAY_NAMES[dayOfWeek];
  const cap = lower.charAt(0).toUpperCase() + lower.slice(1);
  return shopHours[dayOfWeek] ?? shopHours[lower] ?? shopHours[cap];
}

/** The day's tickets that load the shop: dated this day, not on the backlog board, not completed, not deleted. */
export function obliDayTickets(tickets: any[], dayLabel: string, shopTimezone?: string | null): any[] {
  return (tickets || []).filter((t) => {
    const ticketDate = t.scheduled_date
      ?? (t.start_time && shopTimezone ? getShopDayLabel(t.start_time, shopTimezone) : null);
    if (ticketDate !== dayLabel) return false;
    if (isOnBacklogBoard(t)) return false;
    return t.status !== 'Completed' && !isTicketDeleted(t);
  });
}

/** Waiters and tickets needing a specialty resource (ADAS / alignment / specialty bay). */
export function obliStressCounts(dayTickets: any[]): { waiterCount: number; specialtyJobCount: number } {
  let waiterCount = 0;
  let specialtyJobCount = 0;
  for (const ticket of dayTickets) {
    if (ticket.type === 'Waiter') waiterCount++;
    const jobs = ((ticket.ticket_jobs || ticket.jobs) as any[]) || [];
    const hasSpecialty = jobs.some((tj: any) => {
      const job = tj?.job || tj;
      if (!job) return false;
      return job.bay_requirement === 'ADAS' || job.bay_requirement === 'Alignment' || job.bay_requirement === 'Specialty'
        || job.category?.toLowerCase() === 'adas' || job.category === 'alignment';
    });
    if (hasSpecialty) specialtyJobCount++;
  }
  return { waiterCount, specialtyJobCount };
}

export function assembleOBLIDay(args: OBLIDayArgs): OBLIDay {
  const holidays = args.holidays ?? [];
  const isHoliday = holidays.includes(args.dayLabel);
  const roster = (args.technicians || []).filter((t) =>
    (t.employment_status ?? 'Active') === 'Active' && countsAsMechanicalCapacity(t, args.mechanicalTechnicianIds ?? null));

  const capacityByTech = isHoliday ? [] : roster.map((tech) => ({
    tech,
    // Core-100 R4 W5 (CORE-AW Rel): keyed by the SHOP-LOCAL day label, never the device's reading of a Date.
    capacity: resolveTechnicianCapacity(tech, args.dayLabel, 'scheduled'),
    // The same technician with nothing taken away for this day: the nominal day the roster would have worked.
    nominal: resolveTechnicianCapacity({ ...tech, time_off: [], absence_intervals: [], non_recurring_breaks: [] }, args.dayLabel, 'scheduled'),
  }));
  const workingTechs = capacityByTech.filter(({ capacity }) => capacity.state === 'known' && capacity.minutes > 0).map(({ tech }) => tech);
  const unknownHoursCount = capacityByTech.filter(({ capacity }) => capacity.state === 'unknown').length;
  const totalCapacity = capacityByTech.reduce((s, { capacity }) => (capacity.state === 'unknown' ? s : s + capacity.minutes), 0);
  const nominalCapacity = capacityByTech.reduce((s, { nominal, capacity }) =>
    (capacity.state === 'unknown' || nominal.state === 'unknown' ? s : s + nominal.minutes), 0);

  const dayOfWeek = args.selectedDate.getDay();
  const dayHours = shopHoursForDay(args.shopHours, dayOfWeek);
  const shopHoursConfigured = !!args.shopHours && Object.keys(args.shopHours).length > 0;
  const isShopOpen = isHoliday ? false
    : shopHoursConfigured ? !!(dayHours && dayHours.open && dayHours.close)
    // No shop hours on record: an unknown cannot establish CLOSED (CORE-AW §44).
    : workingTechs.length > 0 || unknownHoursCount > 0;

  const dayTickets = obliDayTickets(args.tickets, args.dayLabel, args.shopTimezone);
  const techById = new Map<string, any>();
  for (const t of (args.technicians || [])) techById.set(t.id, t);
  const bookedMinutes = sumEffectiveWorkload(dayTickets, techById, args.nowMs);
  const { waiterCount, specialtyJobCount } = obliStressCounts(dayTickets);

  const capacityReady = args.capacityInputsReady ?? true;
  const input: OBLIInput = {
    capacityKnown: isOBLICapacityKnown({
      rosterLoaded: capacityReady,
      mechanicalRosterKnown: capacityReady,
      knownCapacityMinutes: totalCapacity,
      unknownHoursCount,
    }),
    totalCapacity,
    bookedMinutes,
    ticketCount: dayTickets.length,
    waiterCount,
    specialtyJobCount,
    workingTechCount: workingTechs.length,
    totalTechCount: roster.length,
    scheduledStaffMinutes: totalCapacity,
    nominalStaffMinutes: nominalCapacity,
    isShopOpen,
  };
  return { input, dayTickets, roster, workingTechs, unknownHoursCount, totalCapacity, nominalCapacity };
}
